"use client";

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from "react";
import { User } from "@/gql/graphql";
import useLang from "@/hooks/useLang";

type QuoteContextType = {
  craftman: User | undefined;
  setCraftman: Dispatch<SetStateAction<User | undefined>>;
  message: string;
  setMessage: Dispatch<SetStateAction<string>>;
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  //loadingCraftman: boolean;
  materials: string[] | undefined;
  items: string[] | undefined;
  openSelectMaterials: boolean;
  setOpenSelectMaterials: Dispatch<SetStateAction<boolean>>;
  openSelectItems: boolean;
  setOpenSelectItems: Dispatch<SetStateAction<boolean>>;
  onOpenChangeMat: () => void;
  onOpenChangeIt: () => void;
  quantity: number;
  setQuantity: Dispatch<SetStateAction<number>>;
  itemId: number | undefined;
  materialId: number | undefined;
  categoryId: number;
  selectedValueMat: string;
  setSelectedValueMat: Dispatch<SetStateAction<string>>;
  selectedValueItem: string;
  setSelectedValueItem: Dispatch<SetStateAction<string>>;
  photos: File[];
  setPhotos: Dispatch<SetStateAction<File[]>>;
};

const QuoteContext = createContext<QuoteContextType | undefined>(undefined);

export const QuoteProvider = ({ children }: { children: ReactNode }) => {
  const { lang } = useLang();

  const [selectedValueMat, setSelectedValueMat] = useState<string>("");
  const [selectedValueItem, setSelectedValueItem] = useState<string>("");

  const [craftman, setCraftman] = useState<User | undefined>();
  const [quantity, setQuantity] = useState<number>(1);
  const [photos, setPhotos] = useState<File[]>([]);

  const [openSelectMaterials, setOpenSelectMaterials] = useState(false);
  const [openSelectItems, setOpenSelectItems] = useState(false);
  const onOpenChangeMat = () => {
    setOpenSelectMaterials(!openSelectMaterials);
  };
  const onOpenChangeIt = () => {
    setOpenSelectItems(!openSelectItems);
  };

  // state for messages and modal generate AI
  const [message, setMessage] = useState("");
  const [openModal, setOpenModal] = useState(false);

  // control datas when the selects are opened

  const categoryId = useMemo(
    () => craftman?.Categories[0]?.id || 0,
    [craftman?.Categories]
  );

  const materials = useMemo(() => {
    return craftman?.MaterialTypes?.map(
      (el) => `${lang === "fr" ? el.name_fr : el.name_en}`
    );
  }, [craftman?.MaterialTypes, lang]);

  const items = useMemo(() => {
    return craftman?.ItemTypes?.map(
      (el) => `${lang === "fr" ? el.name_fr : el.name_en}`
    );
  }, [craftman?.ItemTypes, lang]);

  const itemId = useMemo(() => {
    const foundItem = craftman?.ItemTypes?.find(
      (item) =>
        item.name_fr === selectedValueItem || item.name_en === selectedValueItem
    );

    return foundItem?.id ?? 0; // Use nullish coalescing instead of `|| 0`
  }, [craftman?.ItemTypes, selectedValueItem]);

  const materialId = useMemo(() => {
    const foundMaterial = craftman?.MaterialTypes?.find(
      (mat) =>
        mat.name_fr === selectedValueMat || mat.name_en === selectedValueMat
    );

    return foundMaterial?.id ?? 0;
  }, [craftman?.MaterialTypes, selectedValueMat]);

  return (
    <QuoteContext.Provider
      value={{
        craftman,
        setCraftman,
        message,
        setMessage,
        openModal,
        setOpenModal,
        materials,
        items,
        openSelectItems,
        setOpenSelectItems,
        openSelectMaterials,
        setOpenSelectMaterials,
        onOpenChangeMat,
        onOpenChangeIt,
        quantity,
        setQuantity,
        itemId,
        materialId,
        categoryId,
        selectedValueMat,
        setSelectedValueMat,
        selectedValueItem,
        setSelectedValueItem,
        photos,
        setPhotos,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
};

export const useQuoteContext = () => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error("useQuoteContext must be used within a QuoteProvider");
  }
  return context;
};
